import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { MessageSquare, Star, ShieldAlert, CheckCircle, Clock, ShieldOff, RotateCcw, RefreshCw, X, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import client from '@/services/client';
import Pagination, { usePagination } from '@/components/shared/Pagination';
import ScrollHint from '@/components/shared/ScrollHint';

const STATUS_META = {
  pending_llm:   { label: 'Processing',    tone: 'bg-slate-100 dark:bg-zinc-900 text-slate-600 dark:text-zinc-400',    icon: Clock },
  org_preview:   { label: '24h Preview',   tone: 'bg-amber-50 dark:bg-amber-950 text-amber-600 dark:text-amber-400',    icon: Clock },
  live:          { label: 'Live',           tone: 'bg-emerald-50 dark:bg-emerald-950 text-emerald-600 dark:text-emerald-400', icon: CheckCircle },
  flagged:       { label: 'Flagged',        tone: 'bg-orange-50 dark:bg-orange-950 text-orange-600 dark:text-orange-400',    icon: ShieldAlert },
  admin_revoked: { label: 'Admin revoked',  tone: 'bg-red-50 dark:bg-red-950 text-red-600 dark:text-red-400',              icon: ShieldOff },
};

function StarsReadonly({ value }) {
  return (
    <span className="inline-flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((n) => (
        <Star key={n} className={`w-4 h-4 ${n <= value ? 'fill-amber-400 text-amber-400' : 'text-slate-300 dark:text-zinc-700'}`} />
      ))}
    </span>
  );
}

export default function AdminReviews() {
  const [data, setData] = useState({ reviews: [], total: 0, buckets: {} });
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ status: '', search: '' });
  const [drawer, setDrawer] = useState(null);
  const [acting, setActing] = useState(false);
  const p = usePagination(data.reviews, 20);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.status) params.set('status', filters.status);
      if (filters.search) params.set('search', filters.search);
      const res = await client.get(`/admin/reviews?${params.toString()}`);
      setData({ reviews: res.data?.reviews || [], total: res.data?.total || 0, buckets: res.data?.buckets || {} });
    } catch {
      toast.error('Could not load reviews');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load();}, [filters.status]);

  const revoke = async (id) => {
    const reason = window.prompt('Reason (shown internally):', '') || '';
    setActing(true);
    try {
      await client.post(`/admin/reviews/${id}/revoke`, { reason });
      toast.success('Review revoked');
      setDrawer(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed');
    } finally { setActing(false); }
  };
  const restore = async (id) => {
    const reason = window.prompt('Reason (shown internally, e.g. "Reviewed — safe to publish"):', 'Admin approved') || '';
    setActing(true);
    try {
      await client.post(`/admin/reviews/${id}/restore`, { reason });
      toast.success('Review restored to live');
      setDrawer(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed');
    } finally { setActing(false); }
  };

  return (
    <div data-testid="admin-reviews">
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 dark:text-white mb-1 flex items-center gap-2">
            <MessageSquare className="w-6 h-6" /> Candidate Reviews
          </h1>
          <p className="text-slate-500 dark:text-zinc-500 text-sm">
            Auto-moderated reviews from candidates. Flagged ones need attention. Live reviews can be revoked if misuse is discovered later.
          </p>
        </div>
        <button onClick={load} className="text-xs text-slate-500 dark:text-zinc-400 hover:text-slate-900 dark:hover:text-white flex items-center gap-1" data-testid="admin-reviews-refresh">
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="flex gap-1 bg-slate-100 dark:bg-zinc-900 rounded-full p-1">
          {['', 'flagged', 'org_preview', 'live', 'admin_revoked'].map((s) => (
            <button
              key={s || 'all'}
              onClick={() => setFilters((f) => ({ ...f, status: s }))}
              className={`px-3 py-1.5 rounded-full text-[12.5px] font-medium transition ${filters.status === s ? 'bg-white dark:bg-zinc-800 text-slate-900 dark:text-white shadow-sm' : 'text-slate-500 dark:text-zinc-500'}`}
              data-testid={`admin-reviews-tab-${s || 'all'}`}
            >
              {s ? (STATUS_META[s]?.label || s) : 'All'}
              {s && data.buckets?.[s] !== undefined && (
                <span className="ml-1 text-slate-400 dark:text-zinc-500">({data.buckets[s]})</span>
              )}
            </button>
          ))}
        </div>
        <form onSubmit={(e) => { e.preventDefault(); load(); }} className="flex items-center gap-2 bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800 rounded-full px-3 py-1.5">
          <Search className="w-3.5 h-3.5 text-slate-400" />
          <input
            value={filters.search}
            onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
            placeholder="Search email, title, org…"
            className="bg-transparent outline-none text-[13px] text-slate-900 dark:text-white w-56"
            data-testid="admin-reviews-search"
          />
        </form>
      </div>

      <ScrollHint>
      <div className="bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800/50 rounded-lg overflow-x-auto">
        {loading ? (
          <p className="p-8 text-center text-slate-400 text-sm">Loading…</p>
        ) : data.reviews.length === 0 ? (
          <p className="p-10 text-center text-slate-500 dark:text-zinc-500 text-sm">No reviews match the filters.</p>
        ) : (
          <table className="w-full text-sm min-w-[820px]">
            <thead className="border-b border-slate-100 dark:border-zinc-800 text-slate-500 dark:text-zinc-500 text-xs uppercase">
              <tr>
                <th className="text-left px-4 py-3">Review</th>
                <th className="text-left px-4 py-3">Org · Role</th>
                <th className="text-left px-4 py-3">Reviewer</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-left px-4 py-3">Created</th>
                <th className="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {p.pageItems.map((r) => {
                const meta = STATUS_META[r.moderation_status] || STATUS_META.pending_llm;
                const Icon = meta.icon;
                return (
                  <tr key={r.id} className="border-b border-slate-100 dark:border-zinc-900 last:border-0 hover:bg-slate-50 dark:hover:bg-zinc-950" data-testid={`admin-review-row-${r.id}`}>
                    <td className="px-4 py-3 max-w-[380px]">
                      <button onClick={() => setDrawer(r)} className="text-left w-full">
                        <div className="flex items-center gap-2 mb-1"><StarsReadonly value={r.rating} /></div>
                        <div className="font-medium text-slate-900 dark:text-white text-[13px] truncate">{r.title || '(no title)'}</div>
                        <div className="text-[12px] text-slate-500 dark:text-zinc-500 line-clamp-2">{r.body?.slice(0, 160)}</div>
                      </button>
                    </td>
                    <td className="px-4 py-3 text-slate-700 dark:text-zinc-300 text-[12.5px]">
                      <div>{r.org_name || '—'}</div>
                      <div className="text-slate-400 dark:text-zinc-500">{r.job_title || ''}</div>
                    </td>
                    <td className="px-4 py-3 text-slate-700 dark:text-zinc-300 text-[12px] max-w-[180px] truncate">
                      {r.applicant_email || r.applicant_name || '—'}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${meta.tone}`}>
                        <Icon className="w-3 h-3" /> {meta.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-slate-500 dark:text-zinc-500 text-[12px]">
                      {new Date(r.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {r.moderation_status !== 'admin_revoked' ? (
                        <button
                          onClick={() => revoke(r.id)}
                          disabled={acting}
                          className="text-[11px] px-2 py-1 rounded font-medium hover:bg-red-50 dark:hover:bg-red-950 text-red-500 dark:text-red-400"
                          data-testid={`admin-review-revoke-${r.id}`}
                        >Revoke</button>
                      ) : (
                        <button
                          onClick={() => restore(r.id)}
                          disabled={acting}
                          className="text-[11px] px-2 py-1 rounded font-medium hover:bg-emerald-50 dark:hover:bg-emerald-950 text-emerald-600 dark:text-emerald-400"
                          data-testid={`admin-review-restore-${r.id}`}
                        >Restore</button>
                      )}
                      {(r.moderation_status === 'flagged' || r.moderation_status === 'org_preview') && (
                        <button
                          onClick={() => restore(r.id)}
                          disabled={acting}
                          className="ml-1 text-[11px] px-2 py-1 rounded font-medium hover:bg-emerald-50 dark:hover:bg-emerald-950 text-emerald-600 dark:text-emerald-400"
                          data-testid={`admin-review-publish-${r.id}`}
                        >Publish now</button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
      </ScrollHint>

      <Pagination {...p.paginationProps} testIdPrefix="admin-reviews-pg" />

      {drawer && (
        <div className="fixed inset-0 z-50 flex" onClick={() => setDrawer(null)}>
          <div className="flex-1 bg-black/40" />
          <aside
            className="w-full max-w-[560px] h-full overflow-y-auto bg-white dark:bg-[#0d0d10] border-l border-slate-200 dark:border-zinc-800 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
            data-testid="admin-review-drawer"
          >
            <div className="sticky top-0 flex items-center justify-between px-5 py-3 border-b border-slate-100 dark:border-zinc-800 bg-white/95 dark:bg-[#0d0d10]/95 backdrop-blur">
              <div>
                <p className="text-[11px] text-slate-400 uppercase tracking-wide">Review detail</p>
                <h3 className="text-[15px] font-semibold text-slate-900 dark:text-white truncate max-w-[420px]">{drawer.title || '(no title)'}</h3>
              </div>
              <button onClick={() => setDrawer(null)} className="grid place-items-center h-8 w-8 rounded hover:bg-slate-100 dark:hover:bg-zinc-900 text-slate-500"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-5 space-y-4">
              <div className="flex items-center gap-2">
                <StarsReadonly value={drawer.rating} />
                <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${(STATUS_META[drawer.moderation_status] || STATUS_META.pending_llm).tone}`}>
                  {(STATUS_META[drawer.moderation_status] || STATUS_META.pending_llm).label}
                </span>
              </div>
              {drawer.moderation_reason && (
                <div className="p-2.5 rounded-md border border-slate-100 dark:border-zinc-800 text-[12px] text-slate-700 dark:text-zinc-300">
                  <span className="text-slate-400 dark:text-zinc-500 uppercase text-[10px] tracking-wide block mb-0.5">Moderation reason</span>
                  {drawer.moderation_reason}
                </div>
              )}
              {Array.isArray(drawer.moderation_categories) && drawer.moderation_categories.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {drawer.moderation_categories.map((c) => (
                    <span key={c} className="text-[10.5px] px-2 py-0.5 rounded-full bg-orange-50 dark:bg-orange-950 text-orange-600 dark:text-orange-400">{c}</span>
                  ))}
                </div>
              )}
              <div className="space-y-1">
                <p className="text-[11px] uppercase text-slate-400 dark:text-zinc-500 tracking-wide">Body</p>
                <p className="text-[13px] text-slate-800 dark:text-zinc-200 whitespace-pre-wrap">{drawer.body}</p>
              </div>
              <div className="space-y-1">
                <p className="text-[11px] uppercase text-slate-400 dark:text-zinc-500 tracking-wide">Context</p>
                <p className="text-[12.5px] text-slate-700 dark:text-zinc-300">{drawer.org_name || '—'} · {drawer.job_title || '—'}</p>
                <p className="text-[12px] text-slate-500 dark:text-zinc-500">Reviewer: {drawer.applicant_email || drawer.applicant_name || '—'}</p>
                <p className="text-[11px] text-slate-400 dark:text-zinc-500">Terminal state: {drawer.terminal_state}</p>
              </div>
              <div className="pt-2 flex gap-2">
                {drawer.moderation_status !== 'admin_revoked' && (
                  <Button variant="destructive" onClick={() => revoke(drawer.id)} disabled={acting} data-testid="admin-review-drawer-revoke">
                    <ShieldOff className="w-4 h-4 mr-1" /> Revoke
                  </Button>
                )}
                {(drawer.moderation_status === 'flagged' || drawer.moderation_status === 'org_preview' || drawer.moderation_status === 'admin_revoked') && (
                  <Button onClick={() => restore(drawer.id)} disabled={acting} data-testid="admin-review-drawer-restore">
                    <RotateCcw className="w-4 h-4 mr-1" /> Publish now
                  </Button>
                )}
              </div>
            </div>
          </aside>
        </div>
      )}
    </div>
  );
}
